/**
 * Document page (/doc/:slug): rendered markdown plus light editing (title + markdown textarea).
 * Replaces the BlockNote editor in the UI. Formatting markdown cannot carry keeps a document
 * read-only until the user converts it; every first markdown save snapshots the old version.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Eye, FileText, Pencil, PanelRight, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import type { Document } from '@agentwiki/shared'
import { useCreateVersionCheckpoint, useDeleteDocument, useDocumentByRef, useUpdateDocument } from '../hooks/use-documents'
import { useAppStore } from '../stores/app-store'
import { ApiError, apiClient } from '../lib/api-client'
import { useT, type TranslationKey } from '../i18n'
import { cn } from '../lib/utils'
import { contentJsonToMarkdown, findLossyFormatting, hasEditorContent, type LossyFeature } from '../lib/document-markdown'
import { WikiMarkdown } from '../components/wiki/wiki-markdown'
import { Modal } from '../components/ui/modal'
import { AccessLevelBadge } from '../components/documents/access-level-badge'
import { DocumentProperties } from '../components/metadata/document-properties'
import { TagEditor } from '../components/metadata/tag-editor'
import { VersionHistory } from '../components/metadata/version-history'
import { buttonDangerClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, hintClass, inputClass } from '../components/ui/styles'

const LOSSY_LABEL_KEYS: Record<LossyFeature, TranslationKey> = {
  color: 'document.lossy.color',
  underline: 'document.lossy.underline',
  alignment: 'document.lossy.alignment',
  indentation: 'document.lossy.indentation',
  media: 'document.lossy.media',
  imageSize: 'document.lossy.imageSize',
  tableLayout: 'document.lossy.tableLayout',
  unknownBlock: 'document.lossy.unknownBlock',
}

/**
 * Markdown from the stored document: `content`, or the editor JSON converted when no markdown was ever written.
 * A failed conversion is reported as `failed`, never as empty text (empty text could be saved over the page).
 */
function useDocumentMarkdown(doc: Document | undefined, attempt = 0) {
  const [state, setState] = useState<{ key: string; markdown: string | null; failed: boolean } | null>(null)
  const needsDerive = !!doc && !doc.content.trim() && hasEditorContent(doc.contentJson)
  const key = `${doc?.id ?? ''}:${attempt}`

  useEffect(() => {
    if (!needsDerive || !doc) return
    let cancelled = false
    setState({ key, markdown: null, failed: false })
    contentJsonToMarkdown(doc.contentJson)
      .then((markdown) => !cancelled && setState({ key, markdown, failed: false }))
      .catch((err) => {
        console.error('Could not convert the editor JSON to markdown:', err)
        if (!cancelled) setState({ key, markdown: null, failed: true })
      })
    return () => {
      cancelled = true
    }
  }, [needsDerive, doc, key])

  if (!doc) return { markdown: null, failed: false }
  if (!needsDerive) return { markdown: doc.content, failed: false }
  return state?.key === key ? { markdown: state.markdown, failed: state.failed } : { markdown: null, failed: false }
}

/** Remount the page per URL so editing state never carries from one document to another */
export function DocumentRoute() {
  const { slug } = useParams<{ slug: string }>()
  return <DocumentPage key={slug} />
}

export function DocumentPage() {
  const t = useT()
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const [params, setParams] = useSearchParams()
  const locale = useAppStore((s) => s.locale)
  const { data: doc, isLoading, error, isFetching } = useDocumentByRef(slug)
  const [viewAttempt, setViewAttempt] = useState(0)
  const view = useDocumentMarkdown(doc, viewAttempt)

  const lossy = useMemo(() => (doc ? findLossyFormatting(doc.contentJson) : []), [doc])
  const lossyLabels = lossy.map((f) => t(LOSSY_LABEL_KEYS[f])).join(', ')
  const [editing, setEditing] = useState(false)
  const [confirmConvert, setConfirmConvert] = useState(false)
  const [showProperties, setShowProperties] = useState(false)

  // ?edit=1 (set after creating a document) opens the editor for documents that can be edited safely
  useEffect(() => {
    if (doc && params.get('edit') === '1') {
      if (lossy.length === 0) setEditing(true)
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('edit')
        return next
      }, { replace: true })
    }
  }, [doc, lossy.length, params, setParams])

  const startEdit = () => (lossy.length > 0 ? setConfirmConvert(true) : setEditing(true))

  const deleteDocument = useDeleteDocument()
  const handleDelete = async () => {
    if (!doc || !window.confirm(t('document.deleteConfirm', { title: doc.title }))) return
    try {
      await deleteDocument.mutateAsync(doc.id)
      toast.success(t('document.deleted', { title: doc.title }))
      navigate('/')
    } catch (err) {
      toast.error(t('document.deleteFailed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  if (isLoading) {
    return <p className="px-4 py-10 text-center text-sm text-fg-subtle md:px-8">{t('common.loading')}</p>
  }

  if (!doc) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10 md:px-8">
        <p className={errorTextClass} role="alert">
          {error?.status === 404 ? t('document.notFound') : t('document.loadError')}
        </p>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-accent-text hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t('document.back')}
        </Link>
      </div>
    )
  }

  const updated = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(doc.updatedAt))

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      <article className="min-w-0 flex-1 px-4 py-6 md:px-8 md:py-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {editing ? (
            <DocumentEditor
              key={doc.id}
              doc={doc}
              onDone={(newRef) => {
                setEditing(false)
                // A new title gives a new slug; keep the URL valid for refresh and sharing
                if (newRef && newRef !== slug) navigate(`/doc/${encodeURIComponent(newRef)}`, { replace: true })
              }}
            />
          ) : (
            <>
              <header className="space-y-3">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-fg-subtle hover:text-fg">
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  {t('document.back')}
                </Link>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <h1 className="min-w-0 break-words font-display text-2xl font-semibold tracking-tight text-fg md:text-3xl">{doc.title}</h1>
                  <div className="flex shrink-0 gap-2">
                    <button type="button" onClick={() => setShowProperties((v) => !v)} aria-expanded={showProperties} className={cn(buttonSecondaryClass, 'lg:hidden')}>
                      <PanelRight className="h-4 w-4" aria-hidden="true" />
                      {t('document.properties')}
                    </button>
                    <button type="button" onClick={() => void handleDelete()} disabled={deleteDocument.isPending} className={buttonDangerClass}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only sm:not-sr-only">{t('document.delete')}</span>
                    </button>
                    {/* Right after a save the cached copy is stale until the refetch lands; editing it would conflict with that save */}
                    <button type="button" onClick={startEdit} disabled={isFetching} className={buttonPrimaryClass}>
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                      {lossy.length > 0 ? t('document.convert') : t('document.edit')}
                    </button>
                  </div>
                </div>
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-subtle">
                  <AccessLevelBadge level={doc.accessLevel} />
                  <span>{t('document.updated', { date: updated })}</span>
                  {doc.authorName && <span>{t('document.author', { name: doc.authorName })}</span>}
                </p>
                {doc.description && <p className="text-sm leading-relaxed text-fg-muted">{doc.description}</p>}
              </header>

              {lossy.length > 0 && (
                <div role="note" className="flex gap-2 rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-fg-muted">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                  <p>{t('document.lossy.notice', { features: lossyLabels })}</p>
                </div>
              )}

              {view.failed ? (
                <ConvertError onRetry={() => setViewAttempt((n) => n + 1)} />
              ) : view.markdown === null ? (
                <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
              ) : view.markdown.trim() ? (
                <WikiMarkdown content={view.markdown} />
              ) : (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong py-12 text-fg-subtle">
                  <FileText className="h-8 w-8" aria-hidden="true" />
                  <p className="text-sm">{t('document.empty')}</p>
                </div>
              )}
            </>
          )}
        </div>
      </article>

      <aside
        aria-label={t('document.properties')}
        className={cn(
          'shrink-0 border-t border-border bg-surface-muted p-4 lg:block lg:w-80 lg:border-l lg:border-t-0',
          showProperties ? 'block' : 'hidden',
        )}
      >
        {/* Existing properties panel: access level, share link, publish, tags, version history */}
        <div className="flex flex-col gap-4">
          <DocumentProperties documentId={doc.id} readOnly={editing} />
          <div className="border-t border-border" />
          <TagEditor documentId={doc.id} />
          <div className="border-t border-border" />
          <VersionHistory documentId={doc.id} />
        </div>
      </aside>

      {confirmConvert && (
        <Modal title={t('document.convertTitle')} onClose={() => setConfirmConvert(false)} widthClass="max-w-lg">
          <div className="space-y-4 text-sm text-fg-muted">
            <p>{t('document.convertBody', { features: lossyLabels })}</p>
            <p>{t('document.convertSnapshot')}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmConvert(false)} className={buttonSecondaryClass}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={() => {
                  setConfirmConvert(false)
                  setEditing(true)
                }}
                className={buttonPrimaryClass}
              >
                {t('document.convertConfirm')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

/** Title + markdown textarea with a preview tab; explicit Save (Ctrl/⌘+S), no autosave */
function DocumentEditor({ doc, onDone }: { doc: Document; onDone: (newRef?: string) => void }) {
  const t = useT()
  const updateDocument = useUpdateDocument()
  const checkpoint = useCreateVersionCheckpoint()
  const [title, setTitle] = useState(doc.title)
  const [description, setDescription] = useState(doc.description ?? '')
  const [seedAttempt, setSeedAttempt] = useState(0)
  const source = useDocumentMarkdown(doc, seedAttempt)
  const [seed, setSeed] = useState<string | null>(null)
  const [markdown, setMarkdown] = useState('')
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // The version the draft is based on; sent with the save so a newer copy (agent, another member) is never overwritten
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const queryClient = useQueryClient()

  // Agents, the CLI and MCP update `content` without touching `contentJson`, so the markdown is the current copy;
  // the editor JSON is only used when no markdown was ever written (the save checkpoint keeps the JSON either way).
  // Seed once: later refetches must not overwrite the user's edits.
  useEffect(() => {
    if (seed !== null || source.markdown === null) return
    setSeed(source.markdown)
    setMarkdown(source.markdown)
    setBaseUpdatedAt(new Date(doc.updatedAt).toISOString())
  }, [seed, source.markdown, doc.updatedAt])

  const dirty = seed !== null && (markdown !== seed || title.trim() !== doc.title || description.trim() !== (doc.description ?? ''))

  // Set right before a successful save navigates, so leaving after saving is never blocked
  const leavingRef = useRef(false)

  // Warn before closing or reloading the tab with unsaved changes
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  // ...and before in-app navigation (links, sidebar, command palette, browser back) drops the draft
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !leavingRef.current && currentLocation.pathname !== nextLocation.pathname)
  // The blocker object changes identity on every render; react to the state change only, so one blocked
  // navigation asks exactly once
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm(t('document.discardConfirm'))) blockerRef.current.proceed?.()
    else blockerRef.current.reset?.()
  }, [blocker.state, t])

  const save = useCallback(async () => {
    if (seed === null || saving) return
    const nextTitle = title.trim()
    if (!nextTitle) {
      setError(t('document.titleRequired'))
      return
    }
    const nextDescription = description.trim()
    // The API rejects an empty description, so clearing one is not possible here
    if (!nextDescription && doc.description) {
      setError(t('document.descriptionRequired'))
      return
    }
    const renamed = nextTitle !== doc.title
    setSaving(true)
    setError(null)
    setConflict(false)
    try {
      // First markdown save of a BlockNote document: keep both columns in version history, then make markdown the only copy
      const convertsEditorJson = doc.contentJson !== null && doc.contentJson !== undefined
      // An editor JSON without text adds nothing to version history (and the snapshot costs credits)
      if (convertsEditorJson && hasEditorContent(doc.contentJson)) await checkpoint.mutateAsync(doc.id)
      await updateDocument.mutateAsync({
        id: doc.id,
        ...(renamed ? { title: nextTitle } : {}),
        ...(nextDescription && nextDescription !== (doc.description ?? '') ? { description: nextDescription } : {}),
        content: markdown,
        ...(convertsEditorJson ? { contentJson: null } : {}),
        expectedUpdatedAt: baseUpdatedAt ?? new Date(doc.updatedAt).toISOString(),
      })
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.data as { code?: string } | undefined)?.code === 'DOCUMENT_CONFLICT') {
        setConflict(true)
        setSaving(false)
        return
      }
      setError(t('document.saveFailed', { message: err instanceof Error ? err.message : String(err) }))
      setSaving(false)
      return
    }
    setSaving(false)
    leavingRef.current = true
    if (!renamed) return onDone()
    // The update response carries only the id; read the new slug back (the id also resolves /doc/:slug)
    try {
      const fresh = await apiClient.get<Document>(`/api/documents/${doc.id}`)
      onDone(fresh.slug || doc.id)
    } catch {
      onDone(doc.id)
    }
  }, [seed, saving, title, description, markdown, doc, baseUpdatedAt, checkpoint, updateDocument, onDone, t])

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(markdown)
      toast.success(t('document.conflictDraftCopied'))
    } catch {
      toast.error(t('document.conflictCopyFailed'))
    }
  }

  // Drop the draft and reopen the page on the newer copy
  const loadLatest = () => {
    if (!window.confirm(t('document.conflictDiscardConfirm'))) return
    leavingRef.current = true
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
    onDone()
  }

  const cancel = () => {
    if (dirty && !window.confirm(t('document.discardConfirm'))) return
    onDone()
  }

  const tabClass = (active: boolean) =>
    cn('min-h-9 cursor-pointer rounded-md px-3 text-sm font-medium', active ? 'bg-surface-raised text-fg shadow-sm' : 'text-fg-subtle hover:text-fg')

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault()
          void save()
        }
      }}
    >
      <label className="block">
        <span className="sr-only">{t('document.titleLabel')}</span>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={500}
          placeholder={t('document.titleLabel')}
          className={cn(inputClass, 'font-display text-xl font-semibold md:text-2xl')}
        />
      </label>
      {doc.accessLevel === 'public' && title.trim() !== doc.title && <p className={hintClass}>{t('document.renamePublishedKeepsLink')}</p>}
      <label className="block space-y-1.5">
        <span className="text-sm font-medium text-fg-muted">{t('documents.create.description')}</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} rows={2} className={cn(inputClass, 'resize-y')} />
      </label>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label={t('document.editorMode')} className="inline-flex gap-0.5 rounded-lg border border-border bg-surface-muted p-0.5">
          <button type="button" role="tab" aria-selected={tab === 'write'} onClick={() => setTab('write')} className={tabClass(tab === 'write')}>
            <Pencil className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
            {t('document.write')}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'preview'} onClick={() => setTab('preview')} className={tabClass(tab === 'preview')}>
            <Eye className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
            {t('document.preview')}
          </button>
        </div>
        <span className="text-xs text-fg-subtle">{t('document.markdownHint')}</span>
      </div>

      {source.failed && seed === null ? (
        <ConvertError onRetry={() => setSeedAttempt((n) => n + 1)} />
      ) : seed === null ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : tab === 'write' ? (
        <label className="block">
          <span className="sr-only">{t('document.contentLabel')}</span>
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            rows={24}
            spellCheck
            className={cn(inputClass, 'min-h-[50vh] resize-y font-mono text-sm leading-relaxed md:text-sm')}
          />
        </label>
      ) : markdown.trim() ? (
        <div className="min-h-[50vh] rounded-md border border-border p-4">
          <WikiMarkdown content={markdown} />
        </div>
      ) : (
        <p className="min-h-[50vh] rounded-md border border-border p-4 text-sm text-fg-subtle">{t('document.empty')}</p>
      )}

      {error && (
        <p role="alert" className={errorTextClass}>
          {error}
        </p>
      )}
      {conflict && (
        <div role="alert" className="space-y-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning">
          <p className="font-medium">{t('document.conflictTitle')}</p>
          <p>{t('document.conflictBody')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button type="button" onClick={() => void copyDraft()} className="cursor-pointer font-medium underline underline-offset-2">
              {t('document.conflictCopyDraft')}
            </button>
            <button type="button" onClick={loadLatest} className="cursor-pointer font-medium underline underline-offset-2">
              {t('document.conflictLoadLatest')}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={cancel} className={buttonSecondaryClass}>
          {t('common.cancel')}
        </button>
        <button type="submit" disabled={saving || seed === null} className={buttonPrimaryClass}>
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
      </div>
    </form>
  )
}

/** The page text could not be produced from the old editor's JSON: say so, offer a retry, and never show it as empty */
function ConvertError({ onRetry }: { onRetry: () => void }) {
  const t = useT()
  return (
    <div role="alert" className={cn(errorTextClass, 'flex flex-wrap items-center justify-between gap-2')}>
      <span>{t('document.convertFailed')}</span>
      <button type="button" onClick={onRetry} className="cursor-pointer font-medium underline underline-offset-2">
        {t('common.retry')}
      </button>
    </div>
  )
}
